/**
 * The viewer's declared server state: the project's active geo
 * elements, or the scope view when navigating the spatial ladder. Freshness comes from the stream
 * bridge (parsed invalidates this key) -- never from polling.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  type components,
  typedClient,
  throwOnServerError,
  type ElementFilter,
  filterToSearch,
} from "../../api";

/** A text note the drawing carries, placed: the server's own shape. */
export type Callout = components["schemas"]["Callout"];

/** The scope view: one rung of the spatial ladder at one of three depths.  */
export type ScopeView =
  | components["schemas"]["CountsView"]
  | components["schemas"]["RowsView"]
  | components["schemas"]["WholeView"];

export type CountsView = components["schemas"]["CountsView"];
export type RowsView = components["schemas"]["RowsView"];
export type WholeView = components["schemas"]["WholeView"];
export type Subject = components["schemas"]["Subject"];
export type Child = components["schemas"]["Child"];
export type ThinRow = components["schemas"]["ThinRow"];
export type ViewerElement = components["schemas"]["ViewerElement"];
export type FindingView = components["schemas"]["FindingView"];

/** Drawable: either a whole ViewerElement or a ThinRow. Both can be drawn by the canvas. */
export type Drawable = ViewerElement | ThinRow;

/** A whole element carries the authority's payload; a thin row does not. */
export function isWhole(element: Drawable): element is ViewerElement {
  return "raw_attributes" in element;
}

/** One authored text attribute of a whole element, or null when the payload lacks it or it is not text. */
export function textAttribute(element: Drawable, key: string): string | null {
  if (!isWhole(element)) return null;
  const value = Object.hasOwn(element.raw_attributes, key)
    ? Reflect.get(element.raw_attributes, key)
    : undefined;
  return typeof value === "string" && value.length > 0 ? value : null;
}


/** The drawings' own words, placed: every text note the project's documents carry. */
export function useCallouts(projectId: string, scope: string) {
  return useQuery({
    queryKey: ["callouts", projectId, scope],
    queryFn: async (): Promise<Callout[]> => {
      const response = await typedClient.GET("/v1/viewer/callouts", {
        params: { query: { project_id: projectId, scope } },
      });
      throwOnServerError(response);
      return (response.data as Callout[]) ?? [];
    },
  });
}

/** A proposed receptacle as the server pins it. */
export type OutletPin = components["schemas"]["OutletPin"];

/** The receptacle layout the engine proposes: the code's outlets and the practice's, each saying which. */
export function useOutlets(projectId: string, scope: string) {
  return useQuery({
    queryKey: ["outlets", projectId, scope],
    queryFn: async (): Promise<OutletPin[]> => {
      const response = await typedClient.GET("/v1/viewer/outlets", {
        params: { query: { project_id: projectId, scope } },
      });
      throwOnServerError(response);
      return (response.data as OutletPin[]) ?? [];
    },
  });
}

/** The flow as stored: every coupling with its orientation and potentials, and the roles. */
export type FlowView = components["schemas"]["FlowView"];

/** The project's flow (2026-09-13): what the canvas draws as the system when the flow lane is on. */
export function useFlows(projectId: string, scope: string) {
  return useQuery({
    queryKey: ["flows", projectId, scope],
    queryFn: async (): Promise<FlowView> => {
      const response = await typedClient.GET("/v1/viewer/flows", {
        params: { query: { project_id: projectId, scope } },
      });
      throwOnServerError(response);
      return (response.data as FlowView) ?? { edges: [], roles: {} };
    },
  });
}

export type CircuitsView = components["schemas"]["CircuitsView"];

/** The project's drawn circuits (2026-09-15): what the canvas colours apart when the circuits lane is on. */
export function useCircuits(projectId: string, scope: string) {
  return useQuery({
    queryKey: ["circuits", projectId, scope],
    queryFn: async (): Promise<CircuitsView> => {
      const response = await typedClient.GET("/v1/viewer/circuits", {
        params: { query: { project_id: projectId, scope } },
      });
      throwOnServerError(response);
      return (response.data as CircuitsView) ?? { circuits: [] };
    },
  });
}

/** One rung of the spatial ladder at the depth the bytes budget buys: counts, thin rows, or
 * whole elements with findings. Used by the viewer, the catalog, and the findings panel to read
 * at the same rung. */
export function useScopeView(projectId: string, scope: string) {
  return useQuery({
    queryKey: ["scope", projectId, scope],
    queryFn: async (): Promise<ScopeView> => {
      const response = await typedClient.GET("/v1/scope", {
        params: { query: { project_id: projectId, scope } },
      });
      throwOnServerError(response);
      const data = response.data as ScopeView | undefined;
      if (!data) throw new Error(`Invalid scope view response for ${scope}`);
      return data;
    },
  });
}

/** Read and write the current scope from the URL; absent scope means the project rung (the
 * project id itself). */
export function useScope(projectId: string): {
  scope: string;
  setScope: (scope: string) => void;
} {
  const filter = useSearch({ strict: false }) as ElementFilter;
  const navigate = useNavigate();
  const scope = filter.scope ?? projectId;
  const setScope = (newScope: string) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...filterToSearch({ ...filter, scope: newScope }),
      }),
      replace: true,
    });
  };
  return { scope, setScope };
}
